import { AppThunk } from "../../app/types"
import {
  setIsAuthenticated,
  setLoggedInUser,
  setToInitialState,
} from "./authSlice"
import { User } from "./types"
// No imports needed for direct fetch

// Get token from localStorage for API requests
const getTokenFromStorage = (): string | null => {
  try {
    const auth = JSON.parse(localStorage.getItem("persist:msk:auth") || "{}")
    const user = JSON.parse(auth.user || "{}")
    return user.token || null
  } catch (e) {
    console.error("Error getting user token:", e)
    return null
  }
}

// Check if we're in a test session or team simulation context
// These use JWT tokens instead of session cookies
const isSpecialSessionContext = (): boolean => {
  return !!localStorage.getItem("testSessionToken") || !!localStorage.getItem("teamSimulationToken")
}

// Session check action - can be called on app initialization
export const checkSession =
  (): AppThunk<Promise<boolean>> => async (dispatch) => {
    // Skip session check for test sessions and team simulations
    // They use JWT tokens, not session cookies
    if (isSpecialSessionContext()) {
      console.log("Skipping session check - in special session context (test/team simulation)")
      return true
    }

    try {
      // Get token from localStorage - this was the original approach
      const token = getTokenFromStorage()

      // Create headers
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      }

      // Add auth token if available
      if (token) {
        headers["Authorization"] = `Bearer ${token}`
      }

      // Create a direct fetch call instead of trying to use RTK Query in a thunk
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/saas/users/auth/refresh`,
        {
          method: "POST",
          credentials: "include",
          headers,
        },
      )

      const result = await response.json()

      // If refresh succeeded, the session is valid
      // Note: User info would need to be fetched separately if needed
      if (result.code === 200) {
        dispatch(setIsAuthenticated(true))
        return true
      }

      // If we get here, session is invalid
      dispatch(setToInitialState())
      return false
    } catch (error) {
      console.error("Session check failed:", error)
      dispatch(setToInitialState())
      return false
    }
  }

// Login user and store user data in Redux
export const loginUser =
  (user: User): AppThunk =>
  (dispatch) => {
    dispatch(setLoggedInUser(user))
    dispatch(setIsAuthenticated(true))

    // Record session refresh timestamp on login
    localStorage.setItem("session_last_refresh", Date.now().toString())
  }

// Logout user and clear session
export const logoutUser = (): AppThunk => (dispatch) => {
  // Call logout endpoint if available (not part of this implementation)
  // For now, we just clear the Redux state
  dispatch(setToInitialState())
  localStorage.removeItem("persist:msk:auth")
  localStorage.removeItem("session_last_refresh")
}
